import React, { useEffect, useRef, useState } from 'react';

// The filter controls shared by the Project Registers tabs and their customer-portal twins. Every
// filter on those pages is multi-select (Jeremy, 2026-09-03): the questions people actually ask are
// "Risks and Issues", "everything not closed", "these three owners" -- a single-select dropdown made
// each of those two or three passes over the grid.
//
// Filter values are therefore ARRAYS throughout, and an empty array means "no filter", not "nothing
// matches". The filter-key names are plural (`types`, `statuses`, `owners`) so a single-value
// leftover can't slip through unnoticed.

/**
 * Applies one multi-select filter. Empty selection passes everything.
 *
 * `sentinels` handles the options that aren't a plain equality test -- "Unresolved (any)",
 * "(Unassigned)", "In flight (any)". Each is a `{ value, matches(row) }` pair, ORed in with the
 * literal selections, so ticking "Unresolved (any)" and "Canceled" shows both sets rather than
 * their (empty) intersection.
 */
export function matchesSelection(selected, value, row, sentinels = []) {
  if (!selected || selected.length === 0) return true;
  if (selected.includes(value)) return true;
  return sentinels.some((s) => selected.includes(s.value) && s.matches(row));
}

/** True once any filter is set. Arrays and the free-text box, which may be whitespace-only. */
export function anyFilterSet(filters, arrayKeys) {
  return (
    arrayKeys.some((k) => (filters[k] || []).length > 0) || (filters.text || '').trim().length > 0
  );
}

/**
 * A checkbox list behind a dropdown button, rather than the list rendered inline: Status alone has
 * eight values plus a sentinel, and nine permanently-visible checkboxes per filter would push the
 * grid off the first screen.
 */
export function MultiSelect({
  label, options, selected, onChange, allLabel, minWidth = 170, showSelectAll = false,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Closes on a click anywhere else and on Escape. mousedown, not click: a click that starts inside
  // the panel and ends outside it (dragging across a label) shouldn't count as "clicked away".
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  // One selection shows its own label rather than "1 selected" -- the common case shouldn't need a
  // second click to see what it's filtered to.
  const summary = selected.length === 0
    ? allLabel
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? allLabel)
      : `${selected.length} selected`;

  return (
    <div className="form-group" style={{ marginBottom: 0, minWidth, position: 'relative' }} ref={wrapRef}>
      <label className="form-label">{label}</label>
      {/* Styled as a .form-select so it lines up with the search box and every other control on the
          page, but it's a button: a native <select multiple> can't show checkboxes and needs
          ctrl-click to deselect, which nobody discovers. */}
      <button
        type="button"
        className="form-select"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        style={{
          textAlign: 'left', cursor: 'pointer',
          color: selected.length ? 'var(--text-dark)' : 'var(--text-mid)',
        }}
      >
        {summary}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 30, marginTop: 2,
            minWidth: '100%', maxHeight: 260, overflowY: 'auto',
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', boxShadow: '0 6px 18px rgba(0,0,0,0.12)', padding: 6,
          }}
        >
          {options.map((o) => (
            <label
              key={o.value}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', fontSize: 13,
                cursor: 'pointer', whiteSpace: 'nowrap', borderRadius: 4,
              }}
            >
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              {o.label}
            </label>
          ))}
          {showSelectAll && selected.length < options.length && (
            <button
              type="button"
              className="btn btn-outline"
              style={{ fontSize: 11, padding: '3px 8px', margin: '4px 8px 2px', width: 'calc(100% - 16px)' }}
              onClick={() => onChange(options.map((o) => o.value))}
            >
              Select all
            </button>
          )}
          {!!selected.length && (
            <button
              type="button"
              className="btn btn-outline"
              style={{ fontSize: 11, padding: '3px 8px', margin: '4px 8px 2px', width: 'calc(100% - 16px)' }}
              onClick={() => onChange([])}
            >
              Clear {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Free-text search, so the four tabs don't each style their own. */
export function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
      <label className="form-label">Search</label>
      <input
        className="form-input"
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** A plain inline checkbox, aligned with the bottom of the controls beside it. */
export function FilterCheckbox({ label, checked, onChange, title }) {
  return (
    <label
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-mid)',
        cursor: 'pointer', paddingBottom: 10, whiteSpace: 'nowrap',
      }}
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

/** Shown only when a filter is hiding rows, so an empty-looking grid is never a mystery. */
export function FilterCount({ shown, total, noun = 'item' }) {
  return (
    <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 10 }}>
      Showing {shown} of {total} {total === 1 ? noun : `${noun}s`}
    </div>
  );
}
